import React, { useEffect, useState } from "react";
import { Chip, Alert, Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { CustomTable } from "../../components/CustomTable/CustomTable";
import { useAllUsersViewModel } from "./AllUsersViewModel";
import { useLoading } from "../../contexts/LoadingContext";
import { useRbac } from "../../hooks/RbacContext";
import EditUser from "./EditUser";
import Modal from "../../components/Modal/Modal"; // success / error modal

// APIs
import { useRole } from "../../hooks/useRole"; // roles for dropdown
import { useUserApi } from "../../hooks/useUserApi"; // updateUserRole()
import { THEME, MainCard } from "./UserAccessManagement.style";

const AllUsers = () => {
  const { users, loading, error, refetch } = useAllUsersViewModel();
  const { showLoading, hideLoading } = useLoading();
  const { checkAccess } = useRbac();

  const { getRoles } = useRole();
  const { updateUserRole } = useUserApi();

  // dialog state
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // roles for dropdown (passed to EditUser as userAccessManagement)
  const [roles, setRoles] = useState([]);

  // success modal state
  const [successModal, setSuccessModal] = useState(null);

  // preload roles once
  useEffect(() => {
    (async () => {
      try {
        const res = await getRoles();
        const list = res?.data || res?.GetRoles || res?.getAllRoles || [];
        setRoles(Array.isArray(list) ? list : []);
      } catch {
        setRoles([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    { id: "username", label: "Name" },
    { id: "email", label: "Email", align: "left" },
    { id: "designation", label: "Designation", align: "left" },
    {
      id: "created_on",
      label: "Created On",
      align: "left",
      render: (value) => {
        if (!value) return "-";
        const [day, month, year] = value.split("/");
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      },
    },
    {
      id: "updated_on",
      label: "Updated On",
      align: "left",
      render: (value) => {
        if (!value) return "-";
        const [day, month, year] = value.split("/");
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      },
    },
    {
      id: "is_active",
      label: "Status",
      align: "left",
      render: (value) => (
        <Chip
          label={value ? "Active" : "Inactive"}
          size="small"
          sx={{
            height: 24,
            fontSize: "0.75rem",
            fontWeight: 500,
            backgroundColor: value ? "#E8F5E9" : "#FFF3E0",
            color: value ? "#2E7D32" : "#EF6C00",
            "& .MuiChip-label": { px: 1.5 },
          }}
        />
      ),
      searchValue: (value) => (value ? "active" : "inactive"),
    },
  ];

  const fixedColumns = { left: [], right: ["is_active", "actions"] };

  // Actions: gate Edit by userManagement:update
  const getActions = (row) => {
    const actions = [];
    if (checkAccess("userManagement", "update")) {
      actions.push({
        icon: <EditIcon fontSize="small" />,
        label: "Edit",
        onClick: () => {
          setSelectedUser(row);
          setIsEditUserOpen(true);
        },
        sx: {
          color: THEME.primary,
          "&:hover": {
            backgroundColor: THEME.primaryRgba(0.08),
          },
        },
      });
    }
    return actions;
  };

  if (!checkAccess("userManagement", "view")) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          You don't have permission to view Users.
        </Alert>
      </Box>
    );
  }

  // only closes the editor; does not show success
  const handleCloseDialog = () => {
    setIsEditUserOpen(false);
    setSelectedUser(null);
  };

  /**
   * Called by FormValidation on Save.
   * MUST return { success: true } for FormValidation to auto-close.
   * We additionally close the dialog ourselves so it disappears immediately,
   * then show the success modal.
   */
  const handleSubmit = async (payload) => {
    try {
      showLoading();

      // Resolve roleId (payload could have role_id or role_name)
      let roleId =
        payload?.role_id ??
        payload?.roleId ??
        payload?.role_name ??
        payload?.roleName;

      if (isNaN(Number(roleId)) && typeof roleId === "string") {
        const match = roles.find((r) => r.role_name === roleId);
        roleId = match?.role_id;
      }

      if (!selectedUser?.user_id) {
        return { success: false, error: "No user selected." };
      }
      if (!roleId) {
        return { success: false, error: "Please select a role." };
      }

      const res = await updateUserRole({
        user_id: Number(selectedUser.user_id),
        role_id: Number(roleId),
      });
      if (!res?.success) {
        return {
          success: false,
          error: res?.error || "Failed to update user role.",
        };
      }

      // 1) close the dialog immediately (so it doesn't wait for Formik reset)
      setIsEditUserOpen(false);
      setSelectedUser(null);

      // 2) refresh table
      await refetch();

      // 3) show success modal AFTER the dialog is closed
      setSuccessModal({
        type: "success",
        title: "Success",
        message: "Role updated successfully",
      });

      // 4) tell FormValidation it went fine (it will also call onClose internally)
      return { success: true };
    } catch (e) {
      return { success: false, error: e?.message || "Unexpected error." };
    } finally {
      hideLoading();
    }
  };

  if (error) {
    return (
      <MainCard sx={{ p: 1.5 }}>
        <Alert severity="error" sx={{ borderRadius: 8 }}>{error}</Alert>
      </MainCard>
    );
  }

  return (
    <>
    <MainCard sx={{ height: "calc(100vh - 72px)", maxHeight: "calc(100vh - 72px)" }}>
      <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <CustomTable
          columns={columns}
          data={users}
          showActions
          showSearch
          showColumnVisibility={false}
          showAddButton={false}
          pageSize={10}
          stickyHeader
          fixedColumns={fixedColumns}
          actions={getActions}
          title="User Access Management"
          subtitle="Manage and monitor user access"
          alignment="left"
          fontSize="12px"
          showFilters
          compact={true}
        />
      </Box>
    </MainCard>

      {/* Edit allowed only if userManagement:update (dialog opens only via gated action above) */}
      <EditUser
        open={isEditUserOpen}
        onClose={handleCloseDialog}
        initialData={selectedUser}
        userAccessManagement={roles}
        onSubmit={handleSubmit}
      />

      {successModal && (
        <Modal
          type={successModal.type}
          title={successModal.title}
          message={successModal.message}
          onClose={() => setSuccessModal(null)}
        />
      )}
    </>
  );
};

export default AllUsers;
